import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  FormControlLabel,
  Switch,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';
import DataModelDiagram from './DataModelDiagram';
import ModelTableTree from './ModelTableTree';
import ModelRelationshipManager from './ModelRelationshipManager';
import ExcelTemplateExportDialog from './ExcelTemplateExportDialog';
import ExcelTemplateImportDialog from './ExcelTemplateImportDialog';

// Igual que relKey en server/databaseCatalog.js.
function relationshipKey(r) {
  return `${r.fromTableId}|${r.fromColumn}|${r.toTableId}|${r.toColumn}`;
}

function diagramFromCatalog(cat) {
  const tables = (cat.tables || [])
    .filter((t) => t.includedInAI && t.scanStatus !== 'ignored')
    .map((t) => ({
      id: t.id,
      label: t.label || t.name,
      name: t.name,
      schema: t.schema,
      columns: t.columns || [],
    }));
  const modelIds = new Set(tables.map((t) => t.id));
  const relationships = (cat.relationships || [])
    .filter((r) => modelIds.has(r.fromTableId) && modelIds.has(r.toTableId))
    .map((r) => ({ ...r }));
  return { tables, relationships };
}

const DataModelAdmin = forwardRef(function DataModelAdmin(
  { open, getAuthHeaders, embedded = false, onStatusChange },
  ref
) {
  const { showSuccess, showError } = useNotification();
  const [registry, setRegistry] = useState([]);
  const [loadingRegistry, setLoadingRegistry] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [diagram, setDiagram] = useState({ tables: [], relationships: [] });
  const [loadingModel, setLoadingModel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Cambios locales (checkboxes, columnas, relaciones) sin persistir con "Guardar".
  const [dirty, setDirty] = useState(false);
  // Panel derecho de "Modelo de datos": Relaciones (tabla) o Ver Diagrama (mermaid), nunca los dos a la vez.
  const [rightPanelTab, setRightPanelTab] = useState('relaciones');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Guarda a mano cual es la BD seleccionada "de verdad" en este momento, para
  // que una respuesta lenta (scan/detect/load) de una BD que ya no es la
  // seleccionada no pise en pantalla lo que se esta viendo de otra BD.
  const selectedKeyRef = useRef(selectedKey);
  useEffect(() => {
    selectedKeyRef.current = selectedKey;
  }, [selectedKey]);

  const busy = saving || scanning || detecting || deleting;

  const confirmDiscard = useCallback(
    (message) => {
      if (!dirty) return true;
      return window.confirm(
        message || 'Hay cambios sin guardar en este modelo. Se van a perder si continuas. ¿Continuar?'
      );
    },
    [dirty]
  );

  const loadRegistryMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/admin/data-model-registry', { headers }),
  });

  const loadRegistry = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers || !open) return;
    setLoadingRegistry(true);
    try {
      const res = await loadRegistryMutation.mutateAsync(headers);
      if (res.data.success) setRegistry(res.data.entries || []);
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al cargar modelos');
    } finally {
      setLoadingRegistry(false);
    }
  }, [open, getAuthHeaders, showError]);

  const loadModelMutation = useMutation({
    mutationFn: ({ headers, connectionId, database }) =>
      axios.get('/api/admin/data-model', { headers, params: { connectionId, database } }),
  });

  const loadModel = useCallback(
    async (connectionId, database) => {
      const headers = getAuthHeaders?.();
      if (!headers || !connectionId || !database) return;
      const targetKey = `${connectionId}::${database}`;
      setLoadingModel(true);
      try {
        const res = await loadModelMutation.mutateAsync({ headers, connectionId, database });
        // Si mientras esperabamos la respuesta el usuario ya selecciono otra
        // BD, esta respuesta quedo vieja: no pisar lo que se esta mostrando.
        if (res.data.success && selectedKeyRef.current === targetKey) {
          setCatalog(res.data.catalog);
          setDiagram(res.data.diagram || diagramFromCatalog(res.data.catalog));
          setDirty(false);
        }
      } catch (err) {
        if (selectedKeyRef.current === targetKey) {
          showError(err.response?.data?.error || err.message || 'Error al cargar modelo');
        }
      } finally {
        if (selectedKeyRef.current === targetKey) {
          setLoadingModel(false);
        }
      }
    },
    [getAuthHeaders, showError]
  );

  useEffect(() => {
    loadRegistry();
  }, [loadRegistry]);

  useEffect(() => {
    if (!selectedKey) {
      setCatalog(null);
      setDiagram({ tables: [], relationships: [] });
      return;
    }
    const [connectionId, database] = selectedKey.split('::');
    loadModel(connectionId, database);
  }, [selectedKey, loadModel]);

  useEffect(() => {
    if (registry.length > 0 && !selectedKey) {
      const first = registry[0];
      setSelectedKey(`${first.connectionId}::${first.database}`);
    }
  }, [registry, selectedKey]);

  const persistCatalogMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.put('/api/admin/database-catalog', body, { headers }),
  });

  const persistCatalog = async (nextCatalog, successMsg) => {
    const headers = getAuthHeaders?.();
    if (!headers || !nextCatalog) return false;
    const targetKey = `${nextCatalog.connectionId}::${nextCatalog.database}`;
    setSaving(true);
    try {
      const res = await persistCatalogMutation.mutateAsync({
        body: {
          connectionId: nextCatalog.connectionId,
          database: nextCatalog.database,
          catalog: nextCatalog,
        },
        headers,
      });
      if (res.data.success) {
        // Actualizar entrada local del registry sin re-parsear todos los catálogos
        setRegistry((prev) =>
          prev.map((e) =>
            e.connectionId === res.data.catalog.connectionId &&
            e.database === res.data.catalog.database
              ? {
                  ...e,
                  status: res.data.catalog.status,
                  lastScannedAt: res.data.catalog.lastScannedAt,
                  tableCount: res.data.catalog.tables?.length || 0,
                  activeTableCount: (res.data.catalog.tables || []).filter(
                    (t) => t.includedInAI && t.scanStatus !== 'ignored'
                  ).length,
                  hasCatalog: true,
                }
              : e
          )
        );
        if (selectedKeyRef.current === targetKey) {
          setCatalog(res.data.catalog);
          setDiagram(diagramFromCatalog(res.data.catalog));
          setDirty(false);
        }
        if (successMsg) showSuccess(successMsg);
        return true;
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
    return false;
  };

  const handleCatalogChange = (next) => {
    setCatalog(next);
    setDiagram(diagramFromCatalog(next));
    setDirty(true);
  };

  const handleSave = useCallback(
    () => persistCatalog(catalog, 'Modelo guardado'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog]
  );

  // Expone "Guardar" al padre (AdminPanel) para el boton fijo del pie del modal.
  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  useEffect(() => {
    onStatusChange?.({ dirty, saving: busy, canSave: dirty && !busy && Boolean(catalog) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, busy, catalog]);

  const scanCatalogMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/admin/database-catalog/scan', body, { headers }),
  });

  const handleScan = async () => {
    if (!catalog) return;
    if (
      !confirmDiscard(
        'Actualizar el esquema trae la estructura mas reciente de la base de datos y guarda de una. Los cambios sin guardar en este modelo se van a perder. ¿Continuar?'
      )
    ) {
      return;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return;
    const targetKey = `${catalog.connectionId}::${catalog.database}`;
    setScanning(true);
    try {
      const res = await scanCatalogMutation.mutateAsync({
        body: { connectionId: catalog.connectionId, database: catalog.database },
        headers,
      });
      if (res.data.success) {
        if (selectedKeyRef.current === targetKey) {
          setCatalog(res.data.catalog);
          setDiagram(diagramFromCatalog(res.data.catalog));
          setDirty(false);
        }
        showSuccess(`Esquema actualizado: ${res.data.catalog.tables?.length || 0} tablas encontradas`);
        loadRegistry();
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al actualizar esquema');
    } finally {
      if (selectedKeyRef.current === targetKey) {
        setScanning(false);
      }
    }
  };

  const detectRelationshipsMutation = useMutation({
    mutationFn: ({ body, headers }) =>
      axios.post('/api/admin/database-catalog/detect-relationships', body, { headers }),
  });

  const handleDetectRelationships = async () => {
    if (!catalog) return;
    if (
      !confirmDiscard(
        'Detectar relaciones guarda el modelo de una con las relaciones que encuentre. Los cambios sin guardar en este modelo se van a perder. ¿Continuar?'
      )
    ) {
      return;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return;
    const targetKey = `${catalog.connectionId}::${catalog.database}`;
    setDetecting(true);
    try {
      const res = await detectRelationshipsMutation.mutateAsync({
        body: { connectionId: catalog.connectionId, database: catalog.database, autoConfirm: true },
        headers,
      });
      if (res.data.success) {
        if (selectedKeyRef.current === targetKey) {
          setCatalog(res.data.catalog);
          setDiagram(diagramFromCatalog(res.data.catalog));
          setDirty(false);
        }
        const stats = res.data.stats || {};
        showSuccess(
          `Relaciones detectadas: ${stats.confirmed || 0} activas${stats.pending ? `, ${stats.pending} pendientes` : ''}`
        );
        loadRegistry();
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al detectar relaciones');
    } finally {
      if (selectedKeyRef.current === targetKey) {
        setDetecting(false);
      }
    }
  };

  const handleToggleTable = (tableId, included) => {
    if (!catalog) return;
    handleCatalogChange({
      ...catalog,
      tables: catalog.tables.map((t) => (t.id === tableId ? { ...t, includedInAI: included } : t)),
    });
  };

  // Toggle de un esquema completo (checkbox padre): tiene que aplicarse en UNA
  // sola actualizacion de estado. Llamar a handleToggleTable en un forEach
  // (una vez por tabla) dispara N actualizaciones sincronas de setState en el
  // mismo ciclo; como no son funcionales, React solo aplica la ULTIMA de esas
  // N llamadas y descarta el resto -- por eso el checkbox del esquema
  // terminaba marcando/desmarcando una sola tabla en vez de todas.
  const handleToggleTables = (tableIds, included) => {
    if (!catalog) return;
    const idSet = new Set(tableIds);
    handleCatalogChange({
      ...catalog,
      tables: catalog.tables.map((t) => (idSet.has(t.id) ? { ...t, includedInAI: included } : t)),
    });
  };

  const handleUpdateColumn = (tableId, columnName, patch) => {
    if (!catalog) return;
    handleCatalogChange({
      ...catalog,
      tables: catalog.tables.map((t) => {
        if (t.id !== tableId) return t;
        return {
          ...t,
          columns: (t.columns || []).map((c) => {
            if (c.name !== columnName) return c;
            const next = { ...c, ...patch };
            if (patch.contentType !== undefined) {
              next.contentType =
                patch.contentType === 'json' || patch.contentType === 'string'
                  ? patch.contentType
                  : null;
            }
            return next;
          }),
        };
      }),
    });
  };

  const handleUpdateRelationship = (relId, patch) => {
    if (!catalog) return;
    handleCatalogChange({
      ...catalog,
      relationships: (catalog.relationships || []).map((r) => (r.id === relId ? { ...r, ...patch } : r)),
    });
  };

  const handleDeleteRelationship = (relId) => {
    if (!catalog) return;
    const rel = (catalog.relationships || []).find((r) => r.id === relId);
    const dismissed = new Set(catalog.dismissedRelationships || []);
    if (rel) dismissed.add(relationshipKey(rel));
    handleCatalogChange({
      ...catalog,
      relationships: (catalog.relationships || []).filter((r) => r.id !== relId),
      dismissedRelationships: [...dismissed],
    });
  };

  const handleDeleteExternalTable = async (table) => {
    const headers = getAuthHeaders?.();
    if (!headers || !catalog || !table?.id) return;
    if (
      !confirmDiscard(
        'Eliminar la tabla actualiza el modelo guardado. Los cambios sin guardar se van a perder. ¿Continuar?'
      )
    ) {
      return false;
    }
    const targetKey = `${catalog.connectionId}::${catalog.database}`;
    setDeleting(true);
    try {
      const res = await axios.post(
        '/api/admin/data-model/excel-import/delete',
        {
          connectionId: catalog.connectionId,
          database: catalog.database,
          tableId: table.id,
        },
        { headers }
      );
      if (res.data.success && selectedKeyRef.current === targetKey) {
        setCatalog(res.data.catalog);
        setDiagram(diagramFromCatalog(res.data.catalog));
        setDirty(false);
        showSuccess(`Tabla ${table.id} eliminada`);
        loadRegistry();
        return true;
      }
      return true;
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al eliminar la tabla');
      throw err;
    } finally {
      if (selectedKeyRef.current === targetKey) {
        setDeleting(false);
      }
    }
  };

  const handleAddRelationship = (rel) => {
    if (!catalog) return;
    const key = relationshipKey(rel);
    handleCatalogChange({
      ...catalog,
      relationships: [...(catalog.relationships || []), rel],
      dismissedRelationships: (catalog.dismissedRelationships || []).filter((k) => k !== key),
    });
  };

  const modelTableIds = useMemo(
    () =>
      (catalog?.tables || [])
        .filter((t) => t.includedInAI && t.scanStatus !== 'ignored')
        .map((t) => t.id),
    [catalog]
  );

  const needsScan = catalog && (catalog.tables || []).length === 0;
  const scannedTables = useMemo(
    () => (catalog?.tables || []).filter((t) => t && t.scanStatus !== 'removed'),
    [catalog]
  );
  const canExportTemplate = scannedTables.length > 0 && !busy;

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Vista de modelo (estilo Power BI): selecciona tablas, detecta relaciones desde claves foráneas
        y por nombre de columna (p. ej. sensor_id → sensor_id). Solo se confirman automáticamente las
        relaciones plausibles; revisa las pendientes en el diagrama.
      </Typography>

      {loadingRegistry ? (
        <CircularProgress size={24} />
      ) : registry.length === 0 ? (
        <Alert severity="info">
          Configura al menos una conexión con base de datos en &quot;Conexiones y datos&quot;, luego vuelve aquí.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, minHeight: 480 }}>
          <Paper variant="outlined" sx={{ minWidth: 220, maxWidth: 300, p: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>
              Bases de datos
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense disablePadding>
              {registry.map((entry) => {
                const key = `${entry.connectionId}::${entry.database}`;
                return (
                  <ListItemButton
                    key={key}
                    selected={selectedKey === key}
                    disabled={busy}
                    onClick={() => {
                      if (key === selectedKey) return;
                      if (
                        !confirmDiscard(
                          'Hay cambios sin guardar en el modelo actual. Se van a perder si cambias de base de datos. ¿Continuar?'
                        )
                      ) {
                        return;
                      }
                      setSelectedKey(key);
                    }}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={entry.database}
                      secondary={entry.connectionName}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      size="small"
                      label={entry.status === 'active' ? 'Activo' : 'Borrador'}
                      color={entry.status === 'active' ? 'success' : 'default'}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {loadingModel ? (
              <CircularProgress size={24} />
            ) : !catalog ? (
              <Typography variant="body2" color="text.secondary">
                Selecciona una base de datos.
              </Typography>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>
                    {catalog.displayName || catalog.database}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${diagram.tables.length} tablas · ${diagram.relationships.filter((r) => r.confirmed).length} relaciones`}
                  />
                  {dirty && (
                    <Chip size="small" color="warning" label="Cambios sin guardar" />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={catalog.status === 'active'}
                        onChange={(e) =>
                          handleCatalogChange({ ...catalog, status: e.target.checked ? 'active' : 'draft' })
                        }
                        size="small"
                      />
                    }
                    label={catalog.status === 'active' ? 'Modelo activo' : 'Borrador'}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={scanning ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleScan}
                    disabled={busy}
                  >
                    Actualizar esquema
                  </Button>
                  <Tooltip
                    title={modelTableIds.length < 2 ? 'Selecciona al menos 2 tablas en el modelo primero' : ''}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={detecting ? <CircularProgress size={16} /> : <AccountTreeIcon />}
                        onClick={handleDetectRelationships}
                        disabled={busy || modelTableIds.length < 2}
                      >
                        Detectar relaciones
                      </Button>
                    </span>
                  </Tooltip>
                </Box>

                {needsScan && (
                  <Alert severity="warning">
                    Primero pulsa <strong>Actualizar esquema</strong> para cargar las tablas de la base de datos.
                  </Alert>
                )}


                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column', lg: 'row' },
                    // "height" (no maxHeight): en flexbox, flex:1 en los hijos solo
                    // llena el espacio si el padre tiene una altura definida. Con
                    // solo maxHeight, la tabla de relaciones no crecia al minimizar
                    // el diagrama, quedaba espacio en blanco abajo sin usar.
                    height: { xs: 'auto', lg: 560 },
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '100%', lg: 380 },
                      flexShrink: 0,
                      maxHeight: { xs: 320, lg: 560 },
                      minHeight: { xs: 280, lg: 0 },
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <ModelTableTree
                      tables={catalog.tables}
                      onToggleTable={handleToggleTable}
                      onToggleTables={handleToggleTables}
                      onUpdateColumn={handleUpdateColumn}
                      onDeleteExternalTable={handleDeleteExternalTable}
                      deleteBusy={deleting}
                      getAuthHeaders={getAuthHeaders}
                      excelDisabled={!canExportTemplate}
                      excelDisabledReason="Primero pulsa Actualizar esquema para cargar las tablas"
                      onExportExcel={() => setExportDialogOpen(true)}
                      onImportExcel={() => {
                        if (
                          !confirmDiscard(
                            'Importar crea una tabla y actualiza el esquema. Los cambios sin guardar en este modelo se van a perder. ¿Continuar?'
                          )
                        ) {
                          return;
                        }
                        setImportDialogOpen(true);
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Tabs
                      value={rightPanelTab}
                      onChange={(e, v) => setRightPanelTab(v)}
                      sx={{ minHeight: 30, flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}
                    >
                      <Tab value="relaciones" label="Relaciones" sx={{ minHeight: 30, py: 0 }} />
                      <Tab value="diagrama" label="Ver Diagrama" sx={{ minHeight: 30, py: 0 }} />
                    </Tabs>
                    <Box sx={{ flex: 1, minHeight: 0, mt: 0.5, display: 'flex', flexDirection: 'column' }}>
                      {rightPanelTab === 'diagrama' ? (
                        <DataModelDiagram tables={diagram.tables} relationships={diagram.relationships} />
                      ) : (
                        <ModelRelationshipManager
                          relationships={catalog.relationships || []}
                          tables={catalog.tables || []}
                          modelTableIds={modelTableIds}
                          onUpdateRelationship={handleUpdateRelationship}
                          onDeleteRelationship={handleDeleteRelationship}
                          onAddRelationship={handleAddRelationship}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  const withExportDialog = (
    <>
      {content}
      <ExcelTemplateExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        catalog={catalog}
        getAuthHeaders={getAuthHeaders}
      />
      <ExcelTemplateImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        catalog={catalog}
        getAuthHeaders={getAuthHeaders}
        onImported={(nextCatalog) => {
          if (!nextCatalog) return;
          setCatalog(nextCatalog);
          setDiagram(diagramFromCatalog(nextCatalog));
          setDirty(false);
          loadRegistry();
        }}
      />
    </>
  );

  if (embedded) return <Box sx={{ p: 0 }}>{withExportDialog}</Box>;
  return withExportDialog;
});

export default DataModelAdmin;
